import { notifyIcon, number, quote } from "../../../config";
import { deleteProjectVariable } from "../../../shared/services/projectVariables.service";
import { getNotification } from "../../../utils/common";
import { WORKFLOW_VARIABLE_LABELS, newVariableTypes } from "../../Workflow/WorkflowVariables/workflowVariables.constants";
import { deleteWorkflowVariable } from "../../Workflow/WorkflowVariables/services/workflowVariables.service";

const defaultTeamChipFieldType = () => newVariableTypes[0]?.variableType || WORKFLOW_VARIABLE_LABELS.DROPDOWN;

/** Prefer workflow-loaded admin presets, then parent-provided list (same precedence as TeamChips preset picker). */
export const resolveAdminPresetSourceList = (workflowAdminPresets, adminChipPresets) => {
  if (Array.isArray(workflowAdminPresets) && workflowAdminPresets.length > 0) return workflowAdminPresets;
  if (Array.isArray(adminChipPresets) && adminChipPresets.length > 0) return adminChipPresets;
  return [];
};

/**
 * Whether the current user may add, update, or delete chips on the team Chips tab.
 */
export const canUserManageTeamChipsProject = ({ isAdmin, roleId, projectTeamId, userMyProjectId }) => {
  if (isAdmin) return true;
  if (projectTeamId != null && userMyProjectId != null && String(projectTeamId) === String(userMyProjectId)) return true;
  const role = typeof roleId === "string" ? parseInt(roleId, 10) : Number(roleId);
  return  role === number.FOUR;
};

/** Placeholder row when opening “add chip” from parent before save (temp id until API returns). */
export const createTempTeamChipDraft = (chipCreatorType) => {
  const timestamp = Date.now();
  return {
    id: `temp-${timestamp}`,
    name: "",
    usageCount: 0,
    isGlobal: chipCreatorType !== "project",
    fieldType: defaultTeamChipFieldType(),
    description: "",
    required: false,
    numberOnly: false,
    defaultSelection: false,
    options: [""],
  };
};

export const isTeamChipNameTaken = (name, chipsList) => {
  const trimmed = (name || "").trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  return (Array.isArray(chipsList) ? chipsList : []).some((c) => (c?.name || "").toLowerCase() === lower);
};

/**
 * Maps a project_variables document from the API to the Team Chips UI model.
 */
export const mapProjectVariableToTeamChip = (doc) => {
  if (!doc) return null;
  const id = doc._id != null ? String(doc._id) : doc.id != null ? String(doc.id) : null;
  if (!id) return null;
  const type = String(doc.type || "dropdown").toLowerCase();
  const attrs = doc.attributes || {};
  let options = [];
  if (Array.isArray(attrs.options)) {
    options = attrs.options.map((o) => (typeof o === "string" ? o : String(o?.label ?? "")));
  }
  return {
    id,
    name: doc.name || "",
    fieldType: type,
    description: doc.description || "",
    options,
    isGlobal: !!doc.adminType,
    usageCount: typeof attrs.usageCount === "number" ? attrs.usageCount : 0,
    adminType: doc.adminType ?? null,
    variableId: doc.variableId ?? null,
    adminCustomVariables: doc.adminCustomVariables ?? null,
    attributes: doc.attributes ?? null,
  };
};

/**
 * Deletes a variable from the workflow API or, in Team Chips context, from project variables.
 * @returns {Promise<boolean>} false if the project-variable delete API failed (caller should skip local cleanup)
 */
export const deleteWorkflowOrTeamChipVariable = async ({
  selectedVariable,
  workflow,
  isNewWorkflowVariable,
  teamChipPersist,
}) => {
  const rawId = selectedVariable?.id != null ? String(selectedVariable.id) : "";
  const isMongoId = /^[a-f0-9]{24}$/i.test(rawId);

  if (teamChipPersist?.projectId && isMongoId) {
    if (teamChipPersist.allowDelete === false) {
      getNotification(quote.TEAM_CHIPS_MANAGE_DISABLED, notifyIcon.WARNING_ICON);
      return false;
    }
    const result = await deleteProjectVariable(rawId);
    if (result == null) return false;
    teamChipPersist.onAfterDelete?.(rawId);
    return true;
  }

  if (workflow?.Id && !isNewWorkflowVariable) {
    deleteWorkflowVariable({ id: selectedVariable?.id });
  }
  return true;
};
